import React, { useState } from "react";
import {
	Box,
	ButtonBase,
	Chip,
	Collapse,
	Grid,
	IconButton,
	Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryIcon from "@mui/icons-material/History";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { AvatarPersona } from "./AvatarPersona";
import { PersonaDocumentosPanel } from "./PersonaDocumentosPanel";
import { formatDateTimeEs, formatScoreOverHundred } from "../../utils/formatters";

const getFichajeImageEndpoint = (histItem) => {
	if (
		typeof histItem?.url_imagen_endpoint === "string" &&
		histItem.url_imagen_endpoint.trim()
	) {
		return histItem.url_imagen_endpoint.trim();
	}

	const idUsuario = Number(histItem?.id_usuario || 0);
	const idCuadrilla = Number(histItem?.id_cuadrilla || 0);

	if (!idUsuario || !idCuadrilla) {
		return "";
	}

	return `https://recon.jcinformatics.com/imagefichaje.php?id_usuario=${idUsuario}&id_cuadrilla=${idCuadrilla}`;
};

const getEstadoColor = (estado) => {
	if (estado === "validado") return "success";
	if (estado === "no_reconocido") return "error";
	return "warning";
};

const getDocumentacionResumen = (persona) => {
	const resumen = persona?.documentacion_resumen;
	const pendientes = Number(resumen?.pendientes || 0);
	const caducados = Number(resumen?.caducados || 0);

	return {
		vigentes: Number(resumen?.vigentes || 0),
		proximos: Number(resumen?.proximos || 0),
		rojos: Number(resumen?.rojos || pendientes + caducados),
	};
};

const hasPasswordConfigured = (persona) =>
	persona?.tiene_contrasena === true || Number(persona?.tiene_contrasena || 0) === 1;

const hasEmailConfigured = (persona) =>
	typeof persona?.mail === "string" && persona.mail.trim() !== "";

const hasCredencialesActivas = (persona) =>
	Number(persona?.credenciales_activos || 0) === 1 &&
	hasPasswordConfigured(persona) &&
	hasEmailConfigured(persona);

const getPersonaDisplayName = (persona) =>
	[persona?.apellidos, persona?.nombre].filter(Boolean).join(" ").trim() ||
	persona?.nombre_completo ||
	"-";

const DocumentSummaryIcon = ({ color, count, title }) => (
	<Box
		title={title}
		sx={{
			position: "relative",
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			width: 18,
			height: 18,
			flex: "0 0 auto",
		}}
	>
		<InsertDriveFileIcon color={color} sx={{ fontSize: 17 }} />
		<Typography
			component="span"
			sx={{
				position: "absolute",
				top: -4,
				right: -5,
				minWidth: 10,
				px: 0.2,
				borderRadius: 99,
				bgcolor: "background.paper",
				fontSize: "0.58rem",
				fontWeight: 700,
				lineHeight: 1.05,
				textAlign: "center",
				boxShadow: "0 0 0 1px rgba(255,255,255,0.85)",
			}}
		>
			{count}
		</Typography>
	</Box>
);

const SectionToggle = ({ icon, title, subtitle, open, onClick }) => (
	<ButtonBase
		onClick={onClick}
		sx={{
			width: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "space-between",
			gap: 1,
			px: 1.5,
			py: 1.25,
			border: "1px solid",
			borderColor: "divider",
			borderRadius: 1,
			bgcolor: "common.white",
			textAlign: "left",
		}}
	>
		<Box sx={{ display: "flex", alignItems: "center", gap: 1, minWidth: 0 }}>
			{icon}
			<Box sx={{ minWidth: 0 }}>
				<Typography variant="subtitle2">{title}</Typography>
				{subtitle && (
					<Typography variant="caption" color="text.secondary">
						{subtitle}
					</Typography>
				)}
			</Box>
		</Box>

		{open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
	</ButtonBase>
);

export const PersonaCard = ({
	persona,
	isOpen,
	onToggle,
	onAvatarUploaded,
	onOpenDetail,
	onOpenFichajeImage,
	onOpenCredentials,
	folderFilterIds = [],
	loadDocuments,
	uploadDocuments,
	downloadDocument,
	deleteDocument,
}) => {
	const historial = Array.isArray(persona?.historial_fichajes)
		? persona.historial_fichajes
		: [];
	const documentacionResumen = getDocumentacionResumen(persona);
	const [sectionsOpen, setSectionsOpen] = useState({
		historial: false,
		documentos: false,
	});

	const toggleSection = (section) => {
		setSectionsOpen((current) => ({
			...current,
			[section]: !current[section],
		}));
	};
	const detallePersonaUrl = persona?.id ? `/personas/${persona.id}` : undefined;

	return (
		<>
			<Grid
				container
				alignItems="center"
				sx={{
					px: 3,
					py: 1,
					borderBottom: "1px solid",
					borderColor: "divider",
					"&:hover": { bgcolor: "action.hover" },
				}}
			>
				<Grid item size={{ xs: 0.3 }}>
					<IconButton size="small" onClick={onToggle}>
						{isOpen ? <ExpandMoreIcon /> : <ChevronRightIcon />}
					</IconButton>
				</Grid>

				<Grid item size={{ xs: 1.1 }}>
					<Box sx={{ display: "flex", alignItems: "center", gap: 0.35, whiteSpace: "nowrap" }}>
						<DocumentSummaryIcon
							color="success"
							count={documentacionResumen.vigentes}
							title={`Documentos vigentes: ${documentacionResumen.vigentes}`}
						/>
						<DocumentSummaryIcon
							color="warning"
							count={documentacionResumen.proximos}
							title={`Documentos que caducan pronto: ${documentacionResumen.proximos}`}
						/>
						<DocumentSummaryIcon
							color="error"
							count={documentacionResumen.rojos}
							title={`Pendientes o caducados: ${documentacionResumen.rojos}`}
						/>
					</Box>
				</Grid>

				<Grid
					item
					size={{ xs: 2.4 }}
					sx={{ display: "flex", gap: 1, alignItems: "center" }}
				>
					<AvatarPersona
						persona={persona}
						size={26}
						allowUpload
						onAvatarUploaded={onAvatarUploaded}
					/>
					<Typography
						variant="body2"
						noWrap
						component="a"
						href={detallePersonaUrl}
						onClick={(event) => onOpenDetail(persona, event)}
						sx={{
							cursor: "pointer",
							color: "inherit",
							textDecoration: "none",
							"&:hover": {
								textDecoration: "underline",
								color: "primary.main",
							},
						}}
					>
						{getPersonaDisplayName(persona)}
					</Typography>
				</Grid>

				<Grid item size={{ xs: 1.1 }}>
					<Typography variant="body2" noWrap>
						{persona?.nif || "-"}
					</Typography>
				</Grid>

				{/* <Grid item size={{ xs: 1.6 }}>
					<Typography variant="body2" noWrap>
						{persona?.nombre_departamento || "-"}
					</Typography>
				</Grid> */}

				<Grid item size={{ xs: 1.1 }}>
					<Chip size="small" label={persona?.nombre_rol || "-"} />
				</Grid>

				<Grid item size={{ xs: 1.1 }}>
					<Chip
						color={persona?.activo === 1 ? "success" : "error"}
						size="small"
						label={persona?.activo === 1 ? "Si" : "No"}
					/>
				</Grid>

				<Grid item size={{ xs: 1.2 }}>
					<Chip
						color={hasCredencialesActivas(persona) ? "success" : "error"}
						size="small"
						label={hasCredencialesActivas(persona) ? "Si" : "No"}
						onClick={() => onOpenCredentials?.(persona)}
						clickable={typeof onOpenCredentials === "function"}
						title={
							hasCredencialesActivas(persona)
								? "Gestionar credenciales del usuario"
								: hasPasswordConfigured(persona)
								? "Activar credenciales"
								: "Activar credenciales y definir contraseña"
						}
						sx={{
							cursor: typeof onOpenCredentials === "function" ? "pointer" : "default",
						}}
					/>
				</Grid>

				<Grid item size={{ xs: 1.5 }}>
					<Typography variant="body2" noWrap>
						{persona?.mail || "-"}
					</Typography>
				</Grid>
			</Grid>

			<Collapse in={isOpen}>
				<Grid
					container
					sx={{
						bgcolor: "grey.50",
						borderBottom: "1px solid",
						borderColor: "divider",
					}}
				>
					<Box sx={{ width: "100%", px: 3, py: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
						<SectionToggle
							icon={<HistoryIcon fontSize="small" color="action" />}
							title="Historial de fichajes"
							subtitle={
								historial.length > 0
									? `${historial.length} registro(s) recientes`
									: "Sin fichajes recientes"
							}
							open={sectionsOpen.historial}
							onClick={() => toggleSection("historial")}
						/>

						<Collapse in={sectionsOpen.historial}>
							<Box sx={{ px: 1, pt: 1 }}>
								{historial.length === 0 ? (
									<Typography variant="body2" sx={{ opacity: 0.7 }}>
										Sin fichajes recientes.
									</Typography>
								) : (
									<Grid container spacing={1}>
										{historial.map((histItem, index) => {
											const endpoint = getFichajeImageEndpoint(histItem);

											return (
												<Grid
													key={`${histItem.id_cuadrilla}-${histItem.fecha_cuadrilla}-${index}`}
													item
													size={{ xs: 12 }}
												>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 1,
															flexWrap: "wrap",
														}}
													>
														<Typography
															variant="body2"
															sx={{ minWidth: 140 }}
														>
															{formatDateTimeEs(histItem.fecha_cuadrilla)}
														</Typography>
														<Typography
															variant="body2"
															sx={{ opacity: 0.8 }}
														>
															{histItem.nombre_cliente || "-"}
														</Typography>
														<Chip
															size="small"
															label={histItem.estado_label || "-"}
															color={getEstadoColor(histItem.estado)}
														/>
														{histItem?.puntuacion_comprobacion !== undefined &&
															histItem?.puntuacion_comprobacion !== null && (
																<Chip
																	size="small"
																	variant="outlined"
																	label={formatScoreOverHundred(
																		histItem.puntuacion_comprobacion
																	)}
																/>
															)}
														<Chip
															size="small"
															variant="outlined"
															icon={<PhotoCameraIcon />}
															label="Foto"
															disabled={!endpoint}
															clickable={Boolean(endpoint)}
															onClick={
																endpoint
																	? () => onOpenFichajeImage(persona, histItem)
																	: undefined
															}
															title={
																endpoint
																	? "Ver imagen asociada al fichaje"
																	: "Este fichaje no tiene imagen asociada"
															}
														/>
													</Box>
												</Grid>
											);
										})}
									</Grid>
								)}
							</Box>
						</Collapse>

						<SectionToggle
							icon={<DescriptionOutlinedIcon fontSize="small" color="action" />}
							title="Documentos"
							subtitle="Consulta documentos agrupados por campaña y anade nuevos desde esta seccion"
							open={sectionsOpen.documentos}
							onClick={() => toggleSection("documentos")}
						/>

						<Collapse in={sectionsOpen.documentos}>
							<Box sx={{ px: 1, pt: 1 }}>
								<PersonaDocumentosPanel
									persona={persona}
									expanded={sectionsOpen.documentos}
									folderFilterIds={folderFilterIds}
									loadDocuments={loadDocuments}
									allowDropzone
									uploadDocuments={uploadDocuments}
									downloadDocument={downloadDocument}
									deleteDocument={deleteDocument}
								/>
							</Box>
						</Collapse>
					</Box>
				</Grid>
			</Collapse>
		</>
	);
};
